import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { bomberosMaterialMayorAPI, bomberosCompaniaAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Truck, Loader2 } from 'lucide-react';

const tiposVehiculo = [
    { value: 'carro_bomba', label: 'Carro Bomba' },
    { value: 'aljibe', label: 'Aljibe' },
    { value: 'escalera', label: 'Escalera' },
    { value: 'rescate', label: 'Rescate' },
    { value: 'hazmat', label: 'Hazmat' },
    { value: 'comando', label: 'Comando' },
    { value: 'ambulancia', label: 'Ambulancia' },
    { value: 'forestal', label: 'Forestal' },
];

export default function VehiculoFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [companias, setCompanias] = useState([]);
    const [formData, setFormData] = useState({
        codigo: '',
        tipo: 'carro_bomba',
        marca: '',
        modelo: '',
        año: new Date().getFullYear(),
        patente: '',
        numero_chasis: '',
        compania_id: '',
        capacidad_agua: 0,
        capacidad_espuma: 0,
        estado_operativo: 'operativo',
        fecha_revision_tecnica: '',
        fecha_permiso_circulacion: '',
        fecha_seguro: '',
        kilometraje: 0,
        activo: true,
    });

    useEffect(() => {
        loadCompanias();
        if (isEdit) {
            loadVehiculo();
        }
    }, [id]);

    const loadCompanias = async () => {
        try {
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
        } catch (err) {
            toast.error('Error al cargar compañías');
        }
    };

    const loadVehiculo = async () => {
        setLoading(true);
        try {
            const res = await bomberosMaterialMayorAPI.getById(id);
            setFormData({
                codigo: res.data.codigo || '',
                tipo: res.data.tipo || 'carro_bomba',
                marca: res.data.marca || '',
                modelo: res.data.modelo || '',
                año: res.data.año || new Date().getFullYear(),
                patente: res.data.patente || '',
                numero_chasis: res.data.numero_chasis || '',
                compania_id: res.data.compania_id || '',
                capacidad_agua: res.data.capacidad_agua || 0,
                capacidad_espuma: res.data.capacidad_espuma || 0,
                estado_operativo: res.data.estado_operativo || 'operativo',
                fecha_revision_tecnica: res.data.fecha_revision_tecnica || '',
                fecha_permiso_circulacion: res.data.fecha_permiso_circulacion || '',
                fecha_seguro: res.data.fecha_seguro || '',
                kilometraje: res.data.kilometraje || 0,
                activo: res.data.activo !== false,
            });
        } catch (err) {
            toast.error('Error al cargar vehículo');
            navigate('/bomberos/material-mayor');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Solo código, patente y marca son obligatorios - compania_id es opcional
        if (!formData.codigo || !formData.patente || !formData.marca) {
            toast.error('Complete los campos obligatorios (código, patente, marca)');
            return;
        }

        setSaving(true);
        try {
            // Preparar datos - omitir compania_id si está vacío
            const dataToSend = { ...formData };
            if (!dataToSend.compania_id) {
                delete dataToSend.compania_id;
            }
            
            if (isEdit) {
                await bomberosMaterialMayorAPI.update(id, dataToSend);
                toast.success('Vehículo actualizado correctamente');
            } else {
                await bomberosMaterialMayorAPI.create(dataToSend);
                toast.success('Vehículo creado correctamente');
            }
            navigate('/bomberos/material-mayor');
        } catch (err) {
            console.error('Error saving:', err);
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/material-mayor')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Truck className="h-6 w-6" />
                        {isEdit ? 'Editar Vehículo' : 'Nuevo Vehículo'}
                    </h1>
                    <p className="text-muted-foreground">
                        {isEdit ? 'Modifique los datos del vehículo' : 'Complete los datos para registrar un nuevo vehículo'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Identificación */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Identificación del Vehículo</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="codigo">Código *</Label>
                            <Input
                                id="codigo"
                                placeholder="B-1, Z-2, R-1, etc."
                                value={formData.codigo}
                                onChange={(e) => handleChange('codigo', e.target.value.toUpperCase())}
                                data-testid="input-codigo"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="tipo">Tipo *</Label>
                            <Select value={formData.tipo} onValueChange={(v) => handleChange('tipo', v)}>
                                <SelectTrigger data-testid="select-tipo">
                                    <SelectValue placeholder="Seleccione tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tiposVehiculo.map(t => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="patente">Patente *</Label>
                            <Input
                                id="patente"
                                placeholder="ABCD12"
                                value={formData.patente}
                                onChange={(e) => handleChange('patente', e.target.value.toUpperCase())}
                                maxLength={6}
                                data-testid="input-patente"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Datos del Vehículo */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Datos del Vehículo</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="marca">Marca *</Label>
                            <Input
                                id="marca"
                                placeholder="Mercedes-Benz, Volvo, MAN..."
                                value={formData.marca}
                                onChange={(e) => handleChange('marca', e.target.value)}
                                data-testid="input-marca"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="modelo">Modelo</Label>
                            <Input
                                id="modelo"
                                placeholder="Atego, FMX, TGM..."
                                value={formData.modelo}
                                onChange={(e) => handleChange('modelo', e.target.value)}
                                data-testid="input-modelo"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="año">Año</Label>
                            <Input
                                id="año"
                                type="number"
                                min="1950"
                                max={new Date().getFullYear() + 1}
                                value={formData.año}
                                onChange={(e) => handleChange('año', parseInt(e.target.value))}
                                data-testid="input-año"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="numero_chasis">Número de Chasis</Label>
                            <Input
                                id="numero_chasis"
                                placeholder="VIN/Chasis"
                                value={formData.numero_chasis}
                                onChange={(e) => handleChange('numero_chasis', e.target.value.toUpperCase())}
                                data-testid="input-chasis"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="kilometraje">Kilometraje</Label>
                            <Input
                                id="kilometraje"
                                type="number"
                                min="0"
                                value={formData.kilometraje}
                                onChange={(e) => handleChange('kilometraje', parseInt(e.target.value) || 0)}
                                data-testid="input-kilometraje"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="compania_id">Compañía (opcional)</Label>
                            <Select value={formData.compania_id || ""} onValueChange={(v) => handleChange('compania_id', v === "none" ? "" : v)}>
                                <SelectTrigger data-testid="select-compania">
                                    <SelectValue placeholder="Seleccione compañía (opcional)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin asignar</SelectItem>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Capacidades */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Capacidades</CardTitle>
                        <CardDescription>Capacidades de carga del vehículo</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="capacidad_agua">Capacidad de Agua (Litros)</Label>
                            <Input
                                id="capacidad_agua"
                                type="number"
                                min="0"
                                value={formData.capacidad_agua}
                                onChange={(e) => handleChange('capacidad_agua', parseInt(e.target.value) || 0)}
                                data-testid="input-agua"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="capacidad_espuma">Capacidad de Espuma (Litros)</Label>
                            <Input
                                id="capacidad_espuma"
                                type="number"
                                min="0"
                                value={formData.capacidad_espuma}
                                onChange={(e) => handleChange('capacidad_espuma', parseInt(e.target.value) || 0)}
                                data-testid="input-espuma"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Documentación y Estado */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Documentación y Estado</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="estado_operativo">Estado Operativo</Label>
                            <Select value={formData.estado_operativo} onValueChange={(v) => handleChange('estado_operativo', v)}>
                                <SelectTrigger data-testid="select-estado">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="operativo">Operativo</SelectItem>
                                    <SelectItem value="mantenimiento">En Mantenimiento</SelectItem>
                                    <SelectItem value="fuera_servicio">Fuera de Servicio</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="activo">Activo en Inventario</Label>
                            <Select value={formData.activo ? 'true' : 'false'} onValueChange={(v) => handleChange('activo', v === 'true')}>
                                <SelectTrigger data-testid="select-activo">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="true">Activo</SelectItem>
                                    <SelectItem value="false">Dado de Baja</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_revision_tecnica">Fecha Revisión Técnica</Label>
                            <Input
                                id="fecha_revision_tecnica"
                                type="date"
                                value={formData.fecha_revision_tecnica}
                                onChange={(e) => handleChange('fecha_revision_tecnica', e.target.value)}
                                data-testid="input-revision"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_permiso_circulacion">Fecha Permiso Circulación</Label>
                            <Input
                                id="fecha_permiso_circulacion"
                                type="date"
                                value={formData.fecha_permiso_circulacion}
                                onChange={(e) => handleChange('fecha_permiso_circulacion', e.target.value)}
                                data-testid="input-permiso"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_seguro">Fecha Vencimiento Seguro</Label>
                            <Input
                                id="fecha_seguro"
                                type="date"
                                value={formData.fecha_seguro}
                                onChange={(e) => handleChange('fecha_seguro', e.target.value)}
                                data-testid="input-seguro"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button type="button" variant="outline" onClick={() => navigate('/bomberos/material-mayor')}>
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="submit-btn">
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                {isEdit ? 'Actualizar' : 'Crear Vehículo'}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
        </>
    );
}
